import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Tooltip } from "@mui/material";
import { OpenInNewRounded } from "@mui/icons-material";

import { Tr, Td, LargeOnly, SmallOnly, Caret } from "commons";
import { convertToMoney } from "commons/src/Money";
import { isPrintMode } from "commons/src/QueryParameter";

import { FactSheetLink, LinkText, OpenIcon } from "./FactSheetLink";

export function Investment({
    longName,
    isin,
    unit,
    price,
    value,
    split,
    fundFactSheetAvailable = false
}) {
    const [hidden, setHidden] = useState(true);

    return (
        <>
            <DesktopRow
                onClick={() => setHidden(v => !v)}
                expanded={!hidden}
                data-testid="desktop-row"
            >
                {fundFactSheetAvailable && !isPrintMode() ? (
                    <Td align="left">
                        <FlexCell>
                            <LargeOnly>
                                <Tooltip title="View fund fact sheet" placement="right" arrow>
                                    <FactSheetLink isin={isin} fundName={longName}>
                                        <LinkText>{longName}</LinkText>
                                        <OpenIcon />
                                    </FactSheetLink>
                                </Tooltip>
                            </LargeOnly>
                            <SmallOnly>
                                <span>{longName}</span>
                            </SmallOnly>
                        </FlexCell>
                    </Td>
                ) : (
                    <Td align="left">{longName}</Td>
                )}
                <LargeOnly>
                    <Td align="right">{isin}</Td>
                </LargeOnly>
                <LargeOnly>
                    <Td align="right">{unit}</Td>
                </LargeOnly>
                <LargeOnly>
                    <Td align="right">{convertToMoney(price, 6)}</Td>
                </LargeOnly>
                <LargeOnly>
                    <Td align="right">{split}</Td>
                </LargeOnly>
                <ValueTd noWrap align="right" data-testid="investment-value-td">
                    {value}
                    <SmallOnly>
                        <Caret direction={hidden ? "down" : "up"} />
                    </SmallOnly>
                </ValueTd>
            </DesktopRow>
            {!hidden && (
                <SmallOnly>
                    <MobileRow>
                        <Cell colSpan="2">
                            <Detail>
                                <span>ISIN</span>
                                <span>{isin}</span>
                            </Detail>
                        </Cell>
                    </MobileRow>
                    <MobileRow>
                        <Cell colSpan="2">
                            <Detail>
                                <span>Quantity</span>
                                <span>{unit}</span>
                            </Detail>
                        </Cell>
                    </MobileRow>
                    <MobileRow>
                        <Cell colSpan="2">
                            <Detail>
                                <span>Last price</span>
                                <span>{convertToMoney(price, 6)}</span>
                            </Detail>
                        </Cell>
                    </MobileRow>
                    <MobileRow>
                        <Cell colSpan="2">
                            <Detail>
                                <span>Value</span>
                                <span>{value}</span>
                            </Detail>
                        </Cell>
                    </MobileRow>
                    <MobileRow>
                        <Cell colSpan="2">
                            <Detail>
                                <span>Split</span>
                                <span>{split}</span>
                            </Detail>
                        </Cell>
                    </MobileRow>
                    {fundFactSheetAvailable ? (
                        <MobileRow>
                            <Cell colSpan="2">
                                <Detail>
                                    <LinkText>Fund fact sheet</LinkText>
                                    <FactSheetLink isin={isin} fundName={longName}>
                                        <OpenInNewRounded />
                                    </FactSheetLink>
                                </Detail>
                            </Cell>
                        </MobileRow>
                    ) : null}
                </SmallOnly>
            )}
        </>
    );
}

const FlexCell = styled.div`
    display: flex;
`;

const ValueTd = styled(Td)`
    display: flex;
    align-items: center;
    justify-content: flex-end;

    @media (max-width: ${props => props.theme.maxWidth}) {
        && {
            padding-right: 0.5rem;
        }
    }
`;

const bgColor = "rgb(245, 244, 242)";

const DesktopRow = styled(Tr)`
    transition: all 0.1s linear;
    padding: 0 1rem !important;

    @media (prefers-reduced-motion) {
        transition: none;
    }

    @media (max-width: ${props => props.theme.maxWidth}) {
        background-color: ${props => (props.expanded ? bgColor : "#fff")} !important;
    }

    @media (min-width: ${props => props.theme.minWidth}) {
        &:hover {
            background-color: ${bgColor};
        }
    }
`;

const Cell = styled(Td)`
    padding: 0;
`;

const Detail = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 2rem;
`;

export const MobileRow = styled(Tr)`
    background: #fff6ed !important;
`;

Investment.propTypes = {
    longName: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    split: PropTypes.string.isRequired,
    isin: PropTypes.string,
    unit: PropTypes.number.isRequired,
    price: PropTypes.number.isRequired,
    fundFactSheetAvailable: PropTypes.bool
};
